import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { User, UserPlus, Trash2, DollarSign, MessageCircle, Loader2 } from 'lucide-react';
import { useItemContacts, ImmigrationRole } from '@/hooks/useItemContacts';
import { SlotContactSelector } from '@/components/deals/SlotContactSelector';
import { cn } from '@/lib/utils';

interface OnboardingPeopleReviewProps {
  itemId: string;
  catalogId: string;
  readonly?: boolean;
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  spouse: 'Cônjuge',
  child: 'Filho(a)',
  parent: 'Pai/Mãe',
  sibling: 'Irmão/Irmã',
  other: 'Outro',
};

const ROLE_LABELS: Record<ImmigrationRole, string> = {
  applicant: 'Applicant',
  dependent: 'Dependente',
  beneficiary: 'Beneficiário',
  petitioner: 'Peticionário',
  payer: 'Pagador',
  poc: 'Ponto de Contato',
};

export default function OnboardingPeopleReview({ itemId, catalogId, readonly = false }: OnboardingPeopleReviewProps) {
  const {
    contacts,
    isLoading,
    applicant,
    addContact,
    updateContact,
    removeContact,
    ensureFamilyRelationship,
    isAdding,
    isUpdating,
    isRemoving,
  } = useItemContacts(itemId);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRole, setNewRole] = useState<ImmigrationRole>('dependent');
  const [selectedContact, setSelectedContact] = useState<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
  } | null>(null);
  const [relationshipType, setRelationshipType] = useState<string>('spouse');

  const handleAddPerson = async () => {
    if (!selectedContact) return;

    try {
      await addContact({
        item_id: itemId,
        contact_id: selectedContact.id,
        immigration_role: newRole,
        dependent_of: newRole === 'dependent' && applicant ? applicant.contact_id : null,
      });

      // Se for dependente, criar relacionamento familiar
      if (newRole === 'dependent' && applicant) {
        await ensureFamilyRelationship(applicant.contact_id, selectedContact.id, relationshipType);
      }

      setShowAddDialog(false);
      setSelectedContact(null);
      setNewRole('dependent');
      setRelationshipType('spouse');
    } catch (error) {
      console.error('Error adding person:', error);
    }
  };

  const handleRemovePerson = async (contactId: string) => {
    try {
      await removeContact(contactId);
    } catch (error) {
      console.error('Error removing person:', error);
    }
  };

  const handleTogglePayer = async (ic: typeof contacts[0]) => {
    // Primeiro, remover is_payer de todos
    for (const c of contacts) {
      if (c.is_payer && c.id !== ic.id) {
        await updateContact({ id: c.id, is_payer: false });
      }
    }
    // Depois, setar is_payer no contato selecionado
    await updateContact({ id: ic.id, is_payer: !ic.is_payer });
  };

  const handleTogglePOC = async (ic: typeof contacts[0]) => {
    // Primeiro, remover is_point_of_contact de todos
    for (const c of contacts) {
      if (c.is_point_of_contact && c.id !== ic.id) {
        await updateContact({ id: c.id, is_point_of_contact: false });
      }
    }
    // Depois, setar is_point_of_contact no contato selecionado
    await updateContact({ id: ic.id, is_point_of_contact: !ic.is_point_of_contact });
  };

  const getContactName = (contact: typeof contacts[0]['contact']) => {
    return [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Sem nome';
  };

  const getInitials = (contact: typeof contacts[0]['contact']) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'C';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
          <User className="w-4 h-4" />
          <span className="text-sm font-medium">Nenhuma pessoa associada ao item</span>
        </div>
        {!readonly && (
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="outline"
            size="sm"
            className="mt-3 gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Adicionar Pessoa
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Lista de Pessoas */}
      <div className="space-y-2">
        {contacts.map((ic) => (
          <div
            key={ic.id}
            className={cn(
              'flex items-center justify-between p-3 rounded-lg border transition-colors',
              'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
            )}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm">
                  {getInitials(ic.contact)}
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-foreground truncate">
                  {getContactName(ic.contact)}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {ROLE_LABELS[ic.immigration_role] || ic.immigration_role}
                  </Badge>
                  {ic.relationship_type && (
                    <Badge variant="outline" className="text-xs">
                      {RELATIONSHIP_LABELS[ic.relationship_type] || ic.relationship_type}
                    </Badge>
                  )}
                  {ic.is_payer && (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0 text-xs gap-1">
                      <DollarSign className="w-3 h-3" />
                      Pagador
                    </Badge>
                  )}
                  {ic.is_point_of_contact && (
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 border-0 text-xs gap-1">
                      <MessageCircle className="w-3 h-3" />
                      POC
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {!readonly && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 w-8 p-0',
                    ic.is_payer ? 'text-emerald-600' : 'text-muted-foreground hover:text-emerald-600'
                  )}
                  onClick={() => handleTogglePayer(ic)}
                  disabled={isUpdating}
                  title="Definir como Pagador"
                >
                  <DollarSign className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 w-8 p-0',
                    ic.is_point_of_contact ? 'text-blue-600' : 'text-muted-foreground hover:text-blue-600'
                  )}
                  onClick={() => handleTogglePOC(ic)}
                  disabled={isUpdating}
                  title="Definir como Ponto de Contato"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
                {ic.immigration_role !== 'applicant' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        disabled={isRemoving}
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remover pessoa</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja remover {getContactName(ic.contact)} do processo?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleRemovePerson(ic.id)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Remover
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Adicionar Pessoa */}
      {!readonly && (
        <>
          {showAddDialog ? (
            <div className="p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Papel no processo
                  </label>
                  <Select value={newRole} onValueChange={(v) => setNewRole(v as ImmigrationRole)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dependent">Dependente</SelectItem>
                      <SelectItem value="beneficiary">Beneficiário</SelectItem>
                      <SelectItem value="petitioner">Peticionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newRole === 'dependent' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Relacionamento
                    </label>
                    <Select value={relationshipType} onValueChange={setRelationshipType}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spouse">Cônjuge</SelectItem>
                        <SelectItem value="child">Filho(a)</SelectItem>
                        <SelectItem value="parent">Pai/Mãe</SelectItem>
                        <SelectItem value="sibling">Irmão/Irmã</SelectItem>
                        <SelectItem value="other">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Contato
                </label>
                <SlotContactSelector
                  value={selectedContact}
                  onSelectContact={(contact) => setSelectedContact(contact)}
                  onCreateNew={() => {}}
                  placeholder="Buscar contato..."
                  excludeContactIds={contacts.map(c => c.contact_id)}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={handleAddPerson}
                  disabled={!selectedContact || isAdding}
                >
                  {isAdding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Adicionar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowAddDialog(false);
                    setSelectedContact(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowAddDialog(true)}
              variant="outline"
              size="sm"
              className="w-full border-dashed gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Adicionar Pessoa
            </Button>
          )}
        </>
      )}
    </div>
  );
}
